import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useThemeClasses } from "../hooks/useThemeClasses";
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaTwitter,
  FaYoutube,
  FaPinterestP,
} from "react-icons/fa";

const PAYMENT_BADGES = ["UPI", "Visa", "Mastercard", "RuPay", "Net Banking"];

const socialIconClass =
  "flex h-9 w-9 items-center justify-center rounded-md border border-slate-600/80 bg-slate-900/70 text-slate-200 transition-colors hover:border-sky-500/60 hover:bg-slate-800 hover:text-white sm:h-10 sm:w-10";

function FooterNavLink({ to, children }) {
  const { sc } = useThemeClasses();
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `${sc("footerLink")}${isActive ? " !text-amber-300 font-semibold" : ""}`
      }
    >
      {children}
    </NavLink>
  );
}

const Footer = () => {
  const [email, setEmail] = useState("");
  const location = useLocation();
  const { isDark, sc } = useThemeClasses();

  const isPdpRoute = /^\/catalog\/(part|used|body)-p-/.test(location.pathname);
  const mobileBottomPad = isPdpRoute
    ? "max-lg:pb-[calc(10.5rem+env(safe-area-inset-bottom,0px))]"
    : "max-lg:pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))]";

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    setEmail("");
  };

  return (
    <footer className={`${sc("footerShell")} ${mobileBottomPad} lg:pb-0`}>
      {!isDark && (
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-slate-50 to-white"
          aria-hidden="true"
        />
      )}
      {isDark && (
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-b from-sparepe-bg-deep/40 to-sparepe-surface"
          aria-hidden="true"
        />
      )}

      <div className="relative z-10">
        <div className="bg-gradient-to-br from-primary-800 via-primary-600 to-primary-500 py-8 text-black sm:py-10 md:py-12 lg:py-16">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-4 sm:gap-8 sm:px-6 md:flex-row lg:px-8">
            <div className="w-full text-center md:w-auto md:text-left">
              <h2 className="mb-1 text-xl font-bold sm:mb-2 sm:text-2xl md:text-3xl lg:text-4xl">
                Download
              </h2>
              <h3 className="mb-2 text-xl font-bold text-sparepe-bg-deep sm:mb-3 sm:text-2xl md:text-3xl lg:text-4xl">
                Our Mobile App
              </h3>
              <p className="mx-auto mt-2 max-w-md text-xs text-sparepe-bg-deep/80 sm:text-sm md:mx-0 md:text-base lg:text-lg">
                Experience the complete SparePe journey anytime, anywhere.
              </p>
            </div>

            <div className="flex w-full flex-wrap justify-center gap-3 sm:gap-4 md:w-auto">
              <span
                className="inline-flex h-10 items-center rounded-lg bg-black/85 px-4 text-xs font-semibold text-white opacity-90 sm:h-12 sm:text-sm"
                title="Coming soon on the App Store"
              >
                App Store — Coming soon
              </span>
              <span
                className="inline-flex h-10 items-center rounded-lg bg-black/85 px-4 text-xs font-semibold text-white opacity-90 sm:h-12 sm:text-sm"
                title="Coming soon on Google Play"
              >
                Google Play — Coming soon
              </span>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8 md:py-12 lg:px-8 lg:py-16">
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:gap-x-8 md:gap-y-10 lg:grid-cols-4 lg:gap-x-10 lg:gap-y-0 xl:gap-x-12">
            <div className="col-span-2 text-center lg:col-span-1 lg:text-left">
              <Link to="/" className="mb-3 inline-block sm:mb-4">
                <img
                  loading="lazy"
                  src="/sparepe.jpeg"
                  alt="SparePe Logo"
                  className="mx-auto h-10 w-auto cursor-pointer transition-opacity hover:opacity-80 sm:h-12 lg:mx-0"
                />
              </Link>
              <p className={`${sc("footerBlurb")} mx-auto max-w-md lg:mx-0`}>
                India&apos;s online hub for automotive spare parts — OEM, aftermarket, and
                used — with verified sellers and transparent platform fees.
              </p>
            </div>

            <div className="text-left">
              <h3 className="mb-3 text-sm font-bold text-primary-700 sm:mb-4 sm:text-base md:text-lg">
                About
              </h3>
              <ul className="space-y-2 sm:space-y-2.5">
                <li>
                  <FooterNavLink to="/about">About us</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/contact">Contact us</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/faq">FAQ</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/careers">Careers</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/investor-relations">Investor Relations</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/suppliers">Suppliers Relations</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/discovery-points">Discovery Points</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/api-solution">SparePe API Solution</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/signup">Become a Vendor</FooterNavLink>
                </li>
              </ul>
            </div>

            <div className="text-left">
              <h3 className="mb-3 text-sm font-bold text-primary-700 sm:mb-4 sm:text-base md:text-lg">
                Policy
              </h3>
              <ul className="space-y-2 sm:space-y-2.5">
                <li>
                  <FooterNavLink to="/return-policy">Return Policy</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/privacy-policy">Privacy Policy</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/disclaimer">Disclaimer</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/terms-of-use">Terms of Use</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/buyers-policy">Buyers Policy</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/sellers-policy">Sellers Policy</FooterNavLink>
                </li>
              </ul>
            </div>

            <div className="col-span-2 text-left lg:col-span-1">
              <h3 className="mb-3 text-sm font-bold text-primary-700 sm:mb-4 sm:text-base md:text-lg">
                Useful Links
              </h3>
              <ul className="grid grid-cols-2 gap-x-4 gap-y-2 sm:gap-x-6 lg:grid-cols-1 lg:gap-y-2.5">
                <li>
                  <FooterNavLink to="/articles">Articles</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/brands">Brands</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/catalog">Catalogues</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/vehicles">Car Makers</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/damaged-parts">Damaged Parts</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/offers">Best Offers</FooterNavLink>
                </li>
                <li>
                  <FooterNavLink to="/sitemap">Sitemap</FooterNavLink>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className={sc("footerSocialBar")}>
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-5 md:py-6 lg:px-8">
            <div className="flex flex-col items-center justify-between gap-4 sm:gap-5 md:flex-row md:gap-6">
              <div className="flex w-full flex-wrap items-center justify-center gap-2 sm:gap-2.5 md:w-auto md:justify-start md:gap-3">
                <a
                  href="https://facebook.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="Facebook"
                >
                  <FaFacebookF className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://twitter.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="X (Twitter)"
                >
                  <FaTwitter className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://linkedin.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="LinkedIn"
                >
                  <FaLinkedinIn className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="Instagram"
                >
                  <FaInstagram className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://youtube.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="YouTube"
                >
                  <FaYoutube className="text-base sm:text-lg" />
                </a>
                <a
                  href="https://pinterest.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={socialIconClass}
                  title="Pinterest"
                >
                  <FaPinterestP className="text-base sm:text-lg" />
                </a>
              </div>

              <form
                onSubmit={handleNewsletterSubmit}
                className="flex w-full max-w-md flex-col gap-2 sm:w-auto sm:max-w-none sm:flex-row md:justify-end"
              >
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address..."
                  className="flex-1 rounded border border-slate-600 bg-slate-900/80 px-3 py-2 text-xs text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-sky-500 sm:px-4 sm:py-2.5 sm:text-sm sm:flex-none sm:w-56 md:w-64"
                  required
                />
                <button
                  type="submit"
                  className="whitespace-nowrap rounded bg-sky-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-sky-500 sm:px-5 sm:py-2.5 sm:text-sm md:px-6"
                >
                  SUBSCRIBE
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className={sc("footerCopyrightBar")}>
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-5 md:py-6 lg:px-8">
            <div className="flex flex-col items-center justify-between gap-4 sm:gap-5 md:flex-row">
              <div className={`${sc("footerCopyrightText")} order-2 md:order-1`}>
                SparePe © {new Date().getFullYear()}. All rights reserved.
              </div>

              <div className="order-1 flex flex-wrap items-center justify-center gap-2 sm:gap-2.5 md:order-2 md:gap-3">
                {PAYMENT_BADGES.map((label) => (
                  <span
                    key={label}
                    title={label}
                    className="inline-flex h-5 items-center rounded bg-slate-700/90 px-2 text-[10px] font-semibold text-slate-100 opacity-90 transition-opacity hover:opacity-100 sm:h-6"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
